import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import { t } from '../i18n';
import DateSinglePicker from './DateSinglePicker';
import AnimatedCounter from './AnimatedCounter';
import './StatsPage.css';

// ── Назви баз (статичні) ─────────────────────────────────────
const DB_DISPLAY = {
  KV:    { uk: 'Каталог видань',                        en: 'Publications Catalog' },
  KST:   { uk: 'Картотека статей',                      en: 'Article Index' },
  KAD:   { uk: 'Каталог авторефератів та дисертацій',  en: 'Dissertations Catalog' },
  KSZRU: { uk: 'Каталог видань, переданих КЗРУ',           en: 'KSZRU Publications' },
  KWR:   { uk: 'Каталог корисних Інтернет-ресурсів',      en: 'Internet Resources Catalog' },
  OF:    { uk: 'БД Обмінного фонду',                       en: 'Exchange Fund DB' },
};

function dbLabel(code, lang) {
  const entry = DB_DISPLAY[code];
  if (!entry) return code;
  return entry[lang] || entry.uk;
}

const PERIOD_KEYS = ['day', 'week', 'month', 'range'];

// ══════════════════════════════════════════════════════════════
// StatsPage
// ══════════════════════════════════════════════════════════════
export default function StatsPage({ lang, onBack, backLabel }) {
  const today = new Date().toISOString().split('T')[0];

  const [period,     setPeriod]     = useState('day');
  const [customDate, setCustomDate] = useState(today);
  const [rangeFrom,  setRangeFrom]  = useState('');
  const [rangeTo,    setRangeTo]    = useState('');
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(false);
  const [animKey,    setAnimKey]    = useState(0);

  const buildUrl = useCallback(() => {
    let url = `${API_URL}/stats.php?action=get&period=${period}`;
    if (period === 'day')   url += `&date=${encodeURIComponent(customDate)}`;
    if (period === 'range') url += `&from=${encodeURIComponent(rangeFrom)}&to=${encodeURIComponent(rangeTo)}`;
    return url;
  }, [period, customDate, rangeFrom, rangeTo]);

  const fetchStats = useCallback(() => {
    setLoading(true);
    setError(false);
    fetch(buildUrl())
      .then(r => r.json())
      .then(d => {
        if (d.error) throw new Error(d.error);
        setData(d);
        setAnimKey(k => k + 1);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [buildUrl]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // ── Метрика-картка ────────────────────────────────────────
  const metric = (label, value, icon, accent = false) => (
    <div className={`stats-metric${accent ? ' stats-metric--accent' : ''}`}>
      <div className="stats-metric-icon-wrap">
        <i className={`${icon} stats-metric-icon`} />
      </div>
      <div className="stats-metric-body">
        <span className="stats-metric-value">
          {data
            ? <AnimatedCounter key={`${animKey}-${label}`} target={value ?? 0} />
            : <span className="stats-metric-dash">—</span>}
        </span>
        <span className="stats-metric-label">{label}</span>
      </div>
    </div>
  );

  const periodDate = data
    ? (data.period.from === data.period.to
        ? data.period.from
        : `${data.period.from} — ${data.period.to}`)
    : '';

  return (
    <div className="stats-page">

      {/* ── Шапка ─────────────────────────────────────────── */}
      <div className="stats-header">
        {onBack && (
          <button className="stats-back-btn" onClick={onBack}>
            <i className="fa-solid fa-arrow-left" />
            <span>{backLabel || t(lang, 'back_home')}</span>
          </button>
        )}
        <div className="stats-header-main">
          <h1 className="stats-title">
            <i className="fa-solid fa-chart-bar" />
            {t(lang, 'stats_title')}
          </h1>
          {periodDate && <span className="stats-date-badge">{periodDate}</span>}
        </div>
      </div>

      {/* ── Панель вибору періоду ──────────────────────────── */}
      <div className="stats-period-bar">
        <div className="stats-period-tabs" role="tablist">
          {PERIOD_KEYS.map(p => (
            <button
              key={p}
              role="tab"
              aria-selected={period === p}
              className={`stats-period-tab${period === p ? ' stats-period-tab--active' : ''}`}
              onClick={() => setPeriod(p)}
            >
              {t(lang, `stats_period_${p}`)}
            </button>
          ))}
        </div>

        {period === 'day' && (
          <div className="stats-period-pickers">
            <span className="stats-picker-label">{t(lang, 'stats_date_label')}</span>
            <DateSinglePicker
              date={customDate}
              setDate={setCustomDate}
              max={today}
              ariaLabel="Дата"
            />
          </div>
        )}

        {period === 'range' && (
          <>
            <div className="stats-period-pickers">
              <DateSinglePicker
                date={rangeFrom}
                setDate={setRangeFrom}
                max={rangeTo || today}
                ariaLabel="Дата початку"
              />
              <span className="drp-arrow" aria-hidden="true">
                <i className="fa-solid fa-arrow-right" />
              </span>
              <DateSinglePicker
                date={rangeTo}
                setDate={setRangeTo}
                min={rangeFrom}
                max={today}
                ariaLabel="Дата завершення"
              />
            </div>
            <button
              type="button"
              className="stats-clear-range-btn"
              onClick={() => { setRangeFrom(''); setRangeTo(''); }}
            >
              <i className="fa-solid fa-xmark" />
              <span>{t(lang, 'stats_clear_range')}</span>
            </button>
          </>
        )}

        <button
          className="stats-refresh-btn"
          onClick={fetchStats}
          disabled={loading}
          title={t(lang, 'stats_refresh')}
        >
          <i className={`fa-solid fa-rotate${loading ? ' fa-spin' : ''}`} />
          <span className="stats-refresh-label">{t(lang, 'stats_refresh')}</span>
        </button>
      </div>

      {/* ── Помилка ───────────────────────────────────────── */}
      {error && !loading && (
        <div className="stats-error">
          <i className="fa-solid fa-triangle-exclamation" />
          {t(lang, 'stats_error')}
        </div>
      )}

      {/* ── Сітка карток ──────────────────────────────────── */}
      {!error && (
        <div className={`stats-grid${loading && !data ? ' stats-grid--init-loading' : loading ? ' stats-grid--loading' : ''}`}>

          {loading && !data && (
            <div className="stats-init-spinner">
              <i className="fa-solid fa-spinner fa-spin" />
            </div>
          )}

          {/* 1. Відвідувачі */}
          <div className="stats-card stats-card--visitors">
            <div className="stats-card-header">
              <i className="fa-solid fa-users stats-card-icon" />
              <h2 className="stats-card-title">{t(lang, 'stats_visitors')}</h2>
            </div>
            <div className="stats-card-body">
              {metric(t(lang, 'stats_visitors_total'),    data?.visitors?.total,    'fa-solid fa-users',        true)}
              {metric(t(lang, 'stats_visitors_internet'), data?.visitors?.internet, 'fa-solid fa-globe')}
              {metric(t(lang, 'stats_visitors_local'),    data?.visitors?.local,    'fa-solid fa-network-wired')}
            </div>
          </div>

          {/* 2. Запити за видами пошуку */}
          <div className="stats-card stats-card--searches">
            <div className="stats-card-header">
              <i className="fa-solid fa-magnifying-glass stats-card-icon" />
              <h2 className="stats-card-title">{t(lang, 'stats_searches')}</h2>
            </div>
            <div className="stats-card-body">
              {metric(t(lang, 'stats_search_biblio'),   data?.searches?.bibliographic,  'fa-solid fa-book')}
              {metric(t(lang, 'stats_search_fulltext'), data?.searches?.fulltext,        'fa-solid fa-file-lines')}
              {metric(t(lang, 'stats_search_both'),     data?.searches?.biblio_fulltext, 'fa-solid fa-layer-group')}
              {metric(t(lang, 'stats_search_udc'),      data?.searches?.udc,             'fa-solid fa-sitemap')}
            </div>
          </div>

          {/* 3. За видами дій */}
          <div className="stats-card stats-card--actions">
            <div className="stats-card-header">
              <i className="fa-solid fa-bolt stats-card-icon" />
              <h2 className="stats-card-title">{t(lang, 'stats_actions')}</h2>
            </div>
            <div className="stats-card-body">
              {metric(t(lang, 'stats_action_orders'),    data?.actions?.orders,           'fa-solid fa-cart-shopping', true)}
              {metric(t(lang, 'stats_action_ft_unique'), data?.actions?.fulltext_unique,  'fa-solid fa-file-pdf')}
              {metric(t(lang, 'stats_action_ft_reqs'),   data?.actions?.fulltext_reqs,    'fa-solid fa-file-arrow-down')}
              {metric(t(lang, 'stats_action_ft_pages'),  data?.actions?.fulltext_pages,   'fa-regular fa-file')}
            </div>
          </div>

          {/* 4. Звернення по базах */}
          <div className="stats-card stats-card--requests">
            <div className="stats-card-header">
              <i className="fa-solid fa-database stats-card-icon" />
              <h2 className="stats-card-title">{t(lang, 'stats_requests')}</h2>
            </div>
            <div className="stats-card-body">
              <div className="stats-req-total">
                <span className="stats-req-total-label">{t(lang, 'stats_req_total')}</span>
                <span className="stats-req-total-value">
                  {data
                    ? <AnimatedCounter key={`${animKey}-rt`} target={data.requests?.total ?? 0} />
                    : <span className="stats-metric-dash">—</span>}
                </span>
              </div>

              {data && Object.keys(data.requests?.by_db || {}).length > 0 && (
                <div className="stats-db-list">
                  {Object.entries(data.requests.by_db).map(([db, cnt]) => (
                    <div key={db} className="stats-db-row">
                      <span className="stats-db-name">{dbLabel(db, lang)}</span>
                      <span className="stats-db-count">
                        <AnimatedCounter key={`${animKey}-db-${db}`} target={cnt} />
                        <span className="stats-db-views-label"> {t(lang, 'stats_req_views')}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {data && Object.keys(data.requests?.by_db || {}).length === 0 && (
                <p className="stats-no-data">{t(lang, 'stats_no_data')}</p>
              )}
            </div>
          </div>

        </div>
      )}
    </div>
  );
}
